import React from 'react'

import { useSelector } from 'react-redux'
import { createUrl } from 'src/modules/utils/createUrl'
import { Link } from 'gatsby'
import { selectAnalyticsParameters } from 'src/modules/reducers/analytics'
import SEO from 'src/components/common/SEO'
import { LogoSyncContextProvider } from 'src/components/common/AnnotationContext'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import {
  ChannelSection,
  DealSection,
  FAQSection,
  HeroSection,
  Layout,
  NetworkSection,
  PlatformSection,
  FanduelPlanCardsNew,
  ExtraCTABanner,
  MatchTicker,
  RedzoneBanner,
} from 'src/components/common'
import { PlanCards } from 'src/components/common/PlanCards'
import HeroFootNote from 'src/components/common/HeroSectionNew/components/HeroFootNote'

const FuboSportsTemplate = ({
  lang = 'en',
  market = 'us',
  noRedirect,
  miceText,
  // Offer Code
  offerCode,
  offerBanner,
  // Addon Code
  addonCode,
  // Metadata
  pageTitle,
  pageDescription,
  // Hero Section
  logo = 'https://assets-imgx.fubo.tv/marketing/lp/images/logos/fubo-sports-2.png',
  logoHeight = '60px',
  title,
  subtitle,
  tagline,
  ctaText,
  video,
  videoMobile,
  staticAnnotation,
  annotations,
  poster,
  posterMobile,
  noteLink,
  heroCtaNote,
  // Network Section
  networkHeadline,
  networkSubtitle,
  networkSet,
  customNetworks,
  // Others
  team,
  // Show offer input for unique codes
  hasOfferInput,
  // AB Test - Video vs Logo Soup
  noVideo = false,
  // Remove references to cable
  removeCable,
  // Charter Plan Cards
  hasCharterPlanCards,

  // Disable Free Trial
  disableFreeTrial,

  // CA Redirect URL
  caRedirectUrl,
  // SVOD Rate Plan
  ratePlan,
  additionalFAQs,
  hasFanduelPlanCards,
  channelTitle,
  fanduelRSNData,
  dealTitle,
  faqListOverride,
  sportId,
  leagueId,
  teamId,
  planCardSubtitle,
  showFanduelWithPro,
  hasPlanCards,
  plans,
  plansTitle,
  plansSharedData,
  platformImage,
  matchtickerSportId,
  matchtickerLeagueName,
  matchtickerLeagueId,
  matchtickerFilterNetworks,
  showRedzone,
  redzoneTitle,
  redzoneOfferCode,
  redzoneAddOnCode,
  disableAnchor = false,
  channelsAddonCode,
}) => {
  const modifiedCtaText =
    ctaText || (disableFreeTrial && (lang === 'es' ? 'Acceder Ahora' : 'Start Watching'))

  let metaTitle = 'Fubo Sports | Watch Football, Save Money | Try Free'

  let metaDescription =
    pageDescription ||
    'Watch NFL, college football and more without cable. Get FOX, FS1, CBS, ESPN, ESPN+, NFL Network, RedZone and more top sports networks.'

  const metaImage =
    'https://assets-imgx.fubo.tv/marketing/lp/images/meta/1200x628_Fubo_Sports_v1.jpg?auto=format,compress'

  const stateForProtobuf = useSelector(selectStateForProtobuf)
  const handleLocaleSwitch = () => {
    fireUIInteractionEvent(
      {
        element: noteUrl,
        component: 'lp_locale_switch_clicked',
      },
      stateForProtobuf
    )
  }

  const originalParams = useSelector(selectAnalyticsParameters)
  let noteUrl
  if (noteLink) {
    noteUrl = createUrl(noteLink, originalParams)
  }

  let noteText =
    lang === 'es' ? (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        To learn more in English, <span>click here</span>.
      </Link>
    ) : (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        Para traducir a español, <span>haga clic aquí</span>.
      </Link>
    )

  let charterNoteText =
    lang === 'es' ? (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        English
      </Link>
    ) : (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        Español
      </Link>
    )

  const hasAnchor = (hasFanduelPlanCards || hasPlanCards) && !disableAnchor ? '#select-plan' : null

  const hasOffer = offerBanner || offerCode
  if (!hasOffer && fanduelRSNData?.offerCode) {
    offerCode = fanduelRSNData?.offerCode
  }

  const heroFootnote = !disableFreeTrial ? <HeroFootNote lang={lang} showEspn={true} /> : null

  return (
    <Layout
      lang={lang}
      market={market}
      offerCode={offerCode}
      offerBanner={offerBanner}
      channelsAddonCode={channelsAddonCode}
      ctaHrefOverride={hasAnchor}
      noteText={noteUrl && hasCharterPlanCards && charterNoteText}
      noRedirect={noRedirect}
      miceText={miceText}
      caRedirectUrl={caRedirectUrl}
      sportId={sportId}
      leagueId={leagueId}
      teamId={teamId}
    >
      <SEO
        lang={lang}
        market={market}
        title={metaTitle}
        description={metaDescription}
        image={metaImage}
      />
      {matchtickerLeagueId && (
        <MatchTicker
          lang={lang}
          market={market}
          sportId={matchtickerSportId}
          leagueName={matchtickerLeagueName}
          leagueId={matchtickerLeagueId}
          ctaText={modifiedCtaText}
          disableFreeTrial={disableFreeTrial}
          matchtickerFilterNetworks={matchtickerFilterNetworks}
          isFuboSports={true}
          channelsAddonCode={channelsAddonCode}
        />
      )}
      <LogoSyncContextProvider annotations={annotations}>
        <HeroSection
          logo={logo}
          logoHeight={logoHeight}
          lang={lang}
          market={market}
          heroBgVideo={video}
          heroBgVideoMobile={videoMobile}
          title={title}
          subtitle={subtitle}
          tagline={tagline}
          ctaText={modifiedCtaText}
          staticAnnotation={staticAnnotation}
          posterImg={poster}
          posterImgMobile={posterMobile}
          noteText={heroCtaNote || (noteUrl && !hasCharterPlanCards && noteText)}
          hasOfferInput={hasOfferInput}
          noVideo={noVideo}
          ctaHrefOverride={hasAnchor}
          offerCode={offerCode}
          addonCode={addonCode}
          charterNoteText={noteUrl && hasCharterPlanCards && charterNoteText}
          disableFreeTrial={disableFreeTrial}
          footnote={heroFootnote}
        />
      </LogoSyncContextProvider>

      {showRedzone && (
        <RedzoneBanner
          title={redzoneTitle}
          redzoneOfferCode={redzoneOfferCode}
          ctaText={'Add NFL RedZone'}
          disableFreeTrial={disableFreeTrial}
          redzoneAddOnCode={redzoneAddOnCode}
        />
      )}

      {hasFanduelPlanCards && (
        <FanduelPlanCardsNew
          rsnData={fanduelRSNData}
          subtitle={planCardSubtitle}
          showFanduelWithPro={showFanduelWithPro}
        />
      )}

      {hasPlanCards && (
        <PlanCards
          lang={lang}
          market={market}
          plans={plans}
          sharedData={plansSharedData}
          title={plansTitle || 'Which one is right for you?'}
          subtitle={' '}
        />
      )}

      <NetworkSection
        lang={lang}
        market={market}
        headline={networkHeadline || `Watch football and 25+ live channels`}
        subtitle={networkSubtitle}
        networkSet={networkSet || 'fubo_sports'}
        customNetworks={customNetworks}
        ctaText={modifiedCtaText}
        hasOfferInput={hasOfferInput}
        ctaHrefOverride={hasAnchor}
        maxCount={12}
        disableFreeTrial={disableFreeTrial}
      />
      <ChannelSection
        lang={lang}
        market={market}
        title={channelTitle}
        ratePlanCode={ratePlan}
        isFuboSports={true}
        showAllChannels={true}
        channelsAddonCode={channelsAddonCode}
      />

      <DealSection lang={lang} title={dealTitle || 'How is Fubo Sports better than cable TV?'} />

      <PlatformSection
        lang={lang}
        market={market}
        ctaText={modifiedCtaText}
        hasOfferInput={hasOfferInput}
        ctaHrefOverride={hasAnchor}
        title={'Stream live and on-demand from anywhere'}
        platformType="svod"
        disableFreeTrial={disableFreeTrial}
        platformImage={
          platformImage ||
          'https://assets-imgx.fubo.tv/marketing/lp/images/devices/fubosports-devices.png'
        }
      />
      {!disableFreeTrial && (
        <ExtraCTABanner
          hasOfferInput={hasOfferInput}
          ctaText={modifiedCtaText}
          ctaHrefOverride={hasAnchor}
          market={market}
          lang={lang}
          backgroundColor="#4F4FFC"
          disableFreeTrial={disableFreeTrial}
        />
      )}

      <FAQSection
        lang={lang}
        market={market}
        team={team}
        removeCable={removeCable}
        additionalFAQs={additionalFAQs}
        isFuboSports={true}
        faqListOverride={faqListOverride}
      />
    </Layout>
  )
}

export default FuboSportsTemplate
